import type { ContainerStage, FloraDefinition, FloraGrowthDefinition } from '@/flora/FloraDefinition';
import type { FloraInstance } from '@/flora/FloraInstance';
import { computeGrowthState, type GrowthState } from '@/flora/FloraGrowthModel';

/**
 * Derived professional values for the plant inspector (spec §14, milestone 6.5). Pure functions of
 * canonical data so they are unit-testable and can never drift from what the scene renders — the
 * inspector reads exactly the same {@link computeGrowthState} the geometry does.
 */

export interface PlantMetrics {
  growth: GrowthState;
  /** Maturity as a whole-number percentage; may exceed 100 for exceptional specimens. */
  maturityPercent: number;
  matureHeightMeters: number;
  matureSpreadMeters: number;
  /** Estimated age in years, interpolated from the definition's nursery container stages. */
  approximateAgeYears: number;
  /** The container stage this plant is currently closest to, for nursery specification. */
  nearestContainerStage: ContainerStage | null;
  /** Ground area the canopy currently covers, in square meters. */
  canopyAreaSquareMeters: number;
  /** Fraction of mature height reached, in [0, 1+]. */
  fractionOfMatureHeight: number;
}

interface AgePoint {
  height: number;
  age: number;
}

/** Height/age anchors: the nursery container stages plus an extrapolated maturity point. */
function agePoints(growth: FloraGrowthDefinition): AgePoint[] {
  const points: AgePoint[] = growth.containerStages
    .map((stage) => ({ height: stage.heightMeters, age: stage.approximateAgeYears }))
    .sort((a, b) => a.height - b.height);

  const last = points[points.length - 1];
  const typical = growth.matureHeightMeters.typical;
  if (last && typical > last.height) {
    // Prefer the stated annual growth rate; otherwise infer an average rate from the last stage.
    const rate = growth.annualGrowthRateMeters ?? last.height / Math.max(1, last.age);
    points.push({ height: typical, age: last.age + (typical - last.height) / Math.max(0.01, rate) });
  }
  return points;
}

/**
 * Estimate a plant's age from its current height by interpolating the nursery stage curve. Below the
 * smallest stage the curve runs back to the seedling origin; above maturity it continues at the
 * final segment's rate.
 */
export function estimateAgeYears(growth: FloraGrowthDefinition, maturity: number): number {
  const height = computeGrowthState(growth, maturity).heightMeters;
  const points = agePoints(growth);
  const first = points[0];
  if (!first) return 0;
  if (height <= first.height) {
    return first.height > 0 ? Math.max(0, (height / first.height) * first.age) : 0;
  }

  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    if (!previous || !current) continue;
    if (height <= current.height) {
      const span = current.height - previous.height;
      const t = span > 0 ? (height - previous.height) / span : 0;
      return previous.age + (current.age - previous.age) * t;
    }
  }

  // Beyond the last anchor: extend the final segment's growth rate.
  const last = points[points.length - 1];
  const previous = points[points.length - 2] ?? first;
  if (!last) return first.age;
  const span = last.height - previous.height;
  const rate = span > 0 ? (last.age - previous.age) / span : 0;
  return last.age + (height - last.height) * rate;
}

/** The container stage whose height is closest to the plant's current height. */
export function nearestContainerStage(
  growth: FloraGrowthDefinition,
  heightMeters: number,
): ContainerStage | null {
  let best: ContainerStage | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const stage of growth.containerStages) {
    const distance = Math.abs(stage.heightMeters - heightMeters);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = stage;
    }
  }
  return best;
}

export function computePlantMetrics(
  definition: FloraDefinition,
  instance: FloraInstance,
): PlantMetrics {
  const growth = computeGrowthState(definition.growth, instance.maturity, {
    sizeVariation: instance.sizeVariation,
    fullness: instance.fullness,
    health: instance.health,
  });
  const matureHeight = definition.growth.matureHeightMeters.typical;
  const radius = growth.spreadMeters / 2;

  return {
    growth,
    maturityPercent: Math.round(instance.maturity * 100),
    matureHeightMeters: matureHeight,
    matureSpreadMeters: definition.growth.matureSpreadMeters.typical,
    approximateAgeYears: estimateAgeYears(definition.growth, instance.maturity),
    nearestContainerStage: nearestContainerStage(definition.growth, growth.heightMeters),
    canopyAreaSquareMeters: Math.PI * radius * radius,
    fractionOfMatureHeight: matureHeight > 0 ? growth.heightMeters / matureHeight : 0,
  };
}

/** Format an age for display: months below a year, then rounded years. */
export function formatAge(years: number): string {
  if (years < 1) return `${Math.max(1, Math.round(years * 12))} months`;
  if (years < 10) return `${years.toFixed(1)} years`;
  return `${Math.round(years)} years`;
}

/** Day-of-year → a readable calendar label, using a non-leap reference year. */
export function formatDayOfYear(dayOfYear: number): string {
  const date = new Date(Date.UTC(2001, 0, 1));
  date.setUTCDate(Math.max(1, Math.min(365, Math.round(dayOfYear))));
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
